import {
  Component,
  OnInit,
  OnDestroy,
  HostBinding,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef
} from "@angular/core";
import { AdvQueueService } from "@onsip/common/services/api/resources/queue/adv-queue.service";
import { QueueWithExt } from "../../../acdQueuesPage/acd-queues-page.component";
import {
  FormControl,
  AbstractControl,
  Validators,
  NonNullableFormBuilder,
  FormGroup
} from "@angular/forms";
import { BehaviorSubject, Subscription } from "rxjs";
import { SnackbarService } from "../../../../../shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { take } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { ModalMaterialComponent } from "../../../../../shared/components/modal/modal-material.component";
import { StorageServiceService } from "@onsip/common/services/api/resources/storageService/storage-service.service";
import { StorageService } from "@onsip/common/services/api/resources/storageService/storage-service";
import { AdvQueueEmailReportService } from "@onsip/common/services/api/resources/queue/adv-queue-email-report.service";
import { AddStorageServiceDialogComponent } from "../../../addStorageServiceDialog/add-storage-service-dialog.component";
import { views } from "@onsip/web/app/phone/views";
import { ApiAdvQueueEmailReport } from "@onsip/common/services/api/resources/queue/adv-queue-email-report";
import { reportFrequencies, frequencyMap } from "../../../helpers/acd-queues-dropdown-options";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { E2eLocators } from "@onsip/e2e/configs/locators";

@Component({
  selector: "onsip-acd-queues-enhanced-edit",
  templateUrl: "./acd-queues-enhanced-edit.component.html",
  styleUrls: ["./acd-queues-enhanced-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AcdQueuesEnhancedEditComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  @Input() queue!: QueueWithExt;
  @Output() viewPanel = new EventEmitter<void>();
  loadingStateComplete$ = new BehaviorSubject(true);

  E2eLocators = E2eLocators;

  isMobile = false;

  reportFrequencies = reportFrequencies;
  frequencyMap = frequencyMap;

  oneColumn = false;
  views = views;

  originalStorageService: StorageService | undefined;

  queueInfo = this.fb.group({
    queueReporting: [false],
    callRecording: [false],
    storageService: [<StorageService | undefined>undefined],
    recordingAnnouncement: [""],
    recordingTone: [false],
    emailValues: this.fb.group({})
  });

  storageServices: Array<StorageService> = [];

  additionalEmailCount = 0;
  additionalEmailHash: { [counter: number]: Array<string> } = {};
  additionalEmailHashValues: Array<Array<string>> = [];

  existingEmailCount = 0;
  existingEmailHash: { [counter: number]: Array<string> } = {};
  existingEmailHashValues: Array<Array<string>> = [];
  existingEmailControlToId: { [controlName: string]: string } = {};
  removeEmailControlList: Array<string> = [];

  maxEmailReports = 7;

  totalEmailReportChanges = 0;
  emailReportApiSuccessCount = 0;

  private unsubscriber = new Subscription();

  constructor(
    private queueService: AdvQueueService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private storageService: StorageServiceService,
    private emailReportService: AdvQueueEmailReportService,
    private breakpointObserver: BreakpointObserver,
    private cdRef: ChangeDetectorRef,
    private fb: NonNullableFormBuilder
  ) {}

  ngOnInit() {
    this.storageService.storageServiceBrowse();
    this.setBreakpoints();
    this.setFormValues();
    this.unsubscriber.add(
      this.queueInfo.controls.queueReporting.valueChanges.subscribe(value => {
        this.clearReportingFormControls();
        if (value) {
          this.resetReportingGlobals();
          this.setReportingFormValues();
        }
        this.queueInfo.updateValueAndValidity({ emitEvent: false });
      })
    );
    this.unsubscriber.add(
      this.queueInfo.controls.callRecording.valueChanges.subscribe(value => {
        if (!value) {
          this.queueInfo.controls.storageService.clearValidators();
          this.queueInfo.controls.recordingAnnouncement.clearValidators();
          this.queueInfo.controls.storageService.setValue(undefined);
          this.queueInfo.controls.recordingAnnouncement.setValue("");
          this.queueInfo.controls.recordingTone.setValue(false);
        } else {
          this.queueInfo.controls.storageService.setValue(this.originalStorageService);
          this.queueInfo.controls.recordingAnnouncement.setValue(
            this.queue.announceRecording ? this.queue.announceRecording : "basic"
          );
          this.queueInfo.controls.recordingTone.setValue(this.queue.recordingTone);
          this.queueInfo.controls.storageService.setValidators(Validators.required);
        }

        this.queueInfo.updateValueAndValidity({ emitEvent: false });
      })
    );
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  /** getter for email values. need to store these in another FormGroup with unstrict types so we can dynamically change its size */
  get emailValuesForm() {
    return this.queueInfo.get("emailValues") as FormGroup;
  }

  setBreakpoints(): void {
    this.unsubscriber.add(
      this.breakpointObserver.observe(Breakpoints.XSmall).subscribe(result => {
        this.isMobile = result.matches;
        this.cdRef.markForCheck();
      })
    );
  }

  setFormValues(): void {
    this.queueInfo.controls.queueReporting.setValue(
      this.queue.emailReports.length > 0 ? true : false
    );
    this.queueInfo.controls.callRecording.setValue(
      this.queue.storageServiceId === "0" &&
        !this.queue.storageServiceName &&
        !this.queue.storageServiceService
        ? false
        : true
    );
    if (this.queueInfo.controls.queueReporting.value) {
      this.setReportingFormValues();
    }
    this.setRecordingFormValues();
  }

  resetReportingGlobals(): void {
    this.additionalEmailCount = 0;
    this.additionalEmailHash = {};
    this.additionalEmailHashValues = [];
    this.existingEmailCount = 0;
    this.existingEmailHash = {};
    this.existingEmailHashValues = [];
    this.existingEmailControlToId = {};
    this.removeEmailControlList = [];
  }

  clearReportingFormControls(): void {
    this.existingEmailHashValues.map(emailFreq => {
      emailFreq.map(controlName => {
        this.emailValuesForm.removeControl(controlName);
      });
    });
    this.additionalEmailHashValues.map(emailFreq => {
      emailFreq.map(controlName => {
        this.emailValuesForm.removeControl(controlName);
      });
    });
  }

  setReportingFormValues(): void {
    this.existingEmailCount += 1;
    if (this.queue.emailReports.length > 0) {
      // case: array
      this.queue.emailReports.map((emailRep: ApiAdvQueueEmailReport) => {
        const freqControlName = `reportFrequencyExist${this.existingEmailCount.toString()}`;
        const emailControlName = `reportEmailsExist${this.existingEmailCount.toString()}`;
        this.emailValuesForm.addControl(freqControlName, new FormControl("", Validators.required));
        this.emailValuesForm.controls[freqControlName].setValue(
          emailRep.Frequency.substring(0, emailRep.Frequency.indexOf(" "))
        );
        this.emailValuesForm.addControl(emailControlName, new FormControl("", Validators.required));
        this.emailValuesForm.controls[emailControlName].setValue(emailRep.ToAddress);
        this.existingEmailHash[this.existingEmailCount] = [freqControlName, emailControlName];
        this.existingEmailHashValues = Object.values(this.existingEmailHash);
        this.existingEmailControlToId[emailControlName] = emailRep.EmailReportId;
        this.existingEmailCount += 1;
      });
    } else {
      this.addEmail();
    }
  }

  setRecordingFormValues(): void {
    this.unsubscriber.add(
      this.storageService.orgState.subscribe(storageServices => {
        this.storageServices = storageServices;
        this.queueInfo.controls.storageService.setValue(
          storageServices.find(
            service => service.storageServiceId === this.queue.storageServiceId
          ) || storageServices[0]
        );
        this.originalStorageService = this.queueInfo.controls.storageService.value;
      })
    );
    this.queueInfo.controls.recordingAnnouncement.setValue(this.queue.announceRecording);
    this.queueInfo.controls.recordingTone.setValue(this.queue.recordingTone);
  }

  togglePanel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) this.viewPanel.emit();
        })
    );
  }

  showSaveSnackbar(): void {
    this.snackbarService.openSnackBar(
      this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
      "success"
    );
  }

  addStorageService(): void {
    this.dialog.open(AddStorageServiceDialogComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"]
    });
  }

  addEmail(): void {
    this.additionalEmailCount += 1;
    const freqControlName = `reportFrequencyAdd${this.additionalEmailCount.toString()}`;
    const emailControlName = `reportEmailsAdd${this.additionalEmailCount.toString()}`;
    this.emailValuesForm.addControl(freqControlName, new FormControl("", Validators.required));
    this.emailValuesForm.controls[freqControlName].setValue("Daily");
    this.emailValuesForm.addControl(emailControlName, new FormControl("", Validators.required));
    this.additionalEmailHash[this.additionalEmailCount] = [freqControlName, emailControlName];
    this.additionalEmailHashValues = Object.values(this.additionalEmailHash);
  }

  removeEmail(counter: number, type: "existing" | "additional"): void {
    if (type === "additional") {
      delete this.additionalEmailHash[counter];
      this.additionalEmailHashValues = Object.values(this.additionalEmailHash);
      const freqControlName = `reportFrequencyAdd${counter.toString()}`;
      const emailControlName = `reportEmailsAdd${counter.toString()}`;
      this.emailValuesForm.removeControl(freqControlName);
      this.emailValuesForm.removeControl(emailControlName);
    } else if (type === "existing") {
      delete this.existingEmailHash[counter];
      this.existingEmailHashValues = Object.values(this.existingEmailHash);
      const freqControlName = `reportFrequencyExist${counter.toString()}`;
      const emailControlName = `reportEmailsExist${counter.toString()}`;
      this.emailValuesForm.removeControl(freqControlName);
      this.emailValuesForm.removeControl(emailControlName);
      this.removeEmailControlList.push(emailControlName);
    }
    if (this.additionalEmailHashValues.length === 0 && this.existingEmailHashValues.length === 0) {
      this.queueInfo.controls.queueReporting.setValue(false);
    }
  }

  addAddedEmailReports(): void {
    this.additionalEmailHashValues.map(emailFreq => {
      const addParams = {
        AdvQueueId: this.queue.advQueueId,
        ToAddress: this.emailValuesForm.controls[emailFreq[1]].value,
        Frequency: this.frequencyMap[this.emailValuesForm.controls[emailFreq[0]].value]
      };
      this.emailReportService.advQueueEmailReportAdd(addParams).then(result => {
        if (result.status === "success") {
          this.emailReportApiSuccessCount += 1;
          if (this.emailReportApiSuccessCount === this.totalEmailReportChanges) {
            this.editQueue();
          }
        } else {
          this.snackbarService.openSnackBar(result.data.message, "error");
          this.emailReportService.clearErrors();
        }
      });
    });
  }

  editChangedExistingEmailReports(): void {
    for (let i = 1; i <= this.existingEmailCount; i++) {
      const freqControlName = `reportFrequencyExist${i.toString()}`;
      const emailControlName = `reportEmailsExist${i.toString()}`;
      if (this.emailValuesForm.controls[emailControlName]) {
        if (
          this.emailValuesForm.controls[freqControlName].dirty ||
          this.emailValuesForm.controls[emailControlName].dirty
        ) {
          this.editEmailReport(
            this.existingEmailControlToId[emailControlName],
            this.emailValuesForm.controls[emailControlName].value,
            this.frequencyMap[this.emailValuesForm.controls[freqControlName].value]
          );
        }
      }
    }
  }

  editEmailReport(emailReportId: string, toAddress: string, frequency: string): void {
    const editParams = {
      EmailReportId: emailReportId,
      ToAddress: toAddress,
      Frequency: frequency
    };
    this.emailReportService.advQueueEmailReportEdit(editParams).then(result => {
      if (result.status === "success") {
        this.emailReportApiSuccessCount += 1;
        if (this.emailReportApiSuccessCount === this.totalEmailReportChanges) {
          this.editQueue();
        }
      } else {
        this.snackbarService.openSnackBar(result.data.message, "error");
        this.emailReportService.clearErrors();
      }
    });
  }

  removeRemovedEmailReports(): void {
    this.removeEmailControlList.map(emailControl => {
      this.emailReportService
        .advQueueEmailReportDelete(this.existingEmailControlToId[emailControl])
        .then(result => {
          if (result.status === "success") {
            this.emailReportApiSuccessCount += 1;
            if (this.emailReportApiSuccessCount === this.totalEmailReportChanges) {
              this.editQueue();
            }
          } else {
            this.snackbarService.openSnackBar(result.data.message, "error");
            this.emailReportService.clearErrors();
          }
        });
    });
  }

  removeAllEmailReports(): void {
    const emailReportIdList = Object.values(this.existingEmailControlToId);
    emailReportIdList.map(id =>
      this.emailReportService.advQueueEmailReportDelete(id).then(result => {
        if (result.status === "success") {
          this.emailReportApiSuccessCount += 1;
          if (this.emailReportApiSuccessCount === this.totalEmailReportChanges) {
            this.editQueue();
          }
        } else {
          this.snackbarService.openSnackBar(result.data.message, "error");
          this.emailReportService.clearErrors();
        }
      })
    );
  }

  updateEmailReports(): void {
    if (this.additionalEmailHashValues.length > 0) {
      this.addAddedEmailReports();
    }
    if (this.removeEmailControlList.length > 0) {
      this.removeRemovedEmailReports();
    }
    this.editChangedExistingEmailReports();
  }

  cancel(): void {
    const modal = this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      data: {
        title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
        message: this.translate.instant("ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"),
        primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
        secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
        primaryBtnFlat: true
      }
    });

    this.unsubscriber.add(
      modal
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.viewPanel.emit();
          }
        })
    );
  }

  getGenericRequiredError(control: AbstractControl | undefined | null, value: string): string {
    // eslint-disable-next-line no-null/no-null
    if (control === undefined || control === null) return "";
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : "";
  }

  editQueue(): void {
    this.emailReportService.advQueueEmailReportBrowse().then(emailReportResult => {
      if (emailReportResult.status === "success") {
        this.queueService
          .advQueueEdit({
            AdvQueueId: this.queue.advQueueId,
            QueueName: this.queue.queueName,
            MaxCaller: this.queue.maxCaller,
            MaxAgent: this.queue.maxAgent,
            CallerTimeout: this.queue.callerTimeout,
            AgentTimeout: this.queue.agentTimeout,
            RingStrategy: this.queue.ringStrategy,
            WrapupTime: this.queue.wrapupTime,
            MemberDelay: this.queue.memberDelay,
            AnnounceFrequency: this.queue.announceFrequency,
            Username: this.queue.username,
            Password: this.queue.password,
            SmartQueue: this.queue.smartQueue,
            StorageServiceId: this.queueInfo.controls.callRecording.value
              ? this.queueInfo.controls.storageService.value?.storageServiceId
              : "0",
            StorageServiceName: this.queueInfo.controls.callRecording.value
              ? this.queueInfo.controls.storageService.value?.name
              : "",
            StorageServiceService: this.queueInfo.controls.callRecording.value
              ? this.queueInfo.controls.storageService.value?.service
              : "",
            AnnounceRecording: this.queueInfo.controls.callRecording.value
              ? this.queueInfo.controls.recordingAnnouncement.value
              : "",
            RecordingTone: this.queueInfo.controls.recordingTone.value,
            AnnounceHoldTime: this.queue.announceHoldTime,
            ReportHoldTime: this.queue.reportHoldTime,
            EscapeAddress: this.queue.escapeAddress,
            FailoverAddress: this.queue.failoverAddress,
            MusicSourceId: this.queue.musicSourceId,
            RingOnly: this.queue.ringOnly
          })
          .then(result => {
            if (result.status === "success") {
              this.queueService.advQueueBrowse().then(queueBrowseResult => {
                if (queueBrowseResult.status === "success") {
                  this.cdRef.markForCheck();
                  this.viewPanel.emit();
                }
              });

              this.showSaveSnackbar();
            } else {
              this.snackbarService.openSnackBar(result.data.message, "error");
              this.queueService.clearErrors();
            }
            this.loadingStateComplete$.next(true);
          });
      } else {
        this.snackbarService.openSnackBar(emailReportResult.data.message, "error");
        this.emailReportService.clearErrors();
      }
    });
  }

  setTotalEmailReportChanges(): void {
    this.totalEmailReportChanges =
      this.additionalEmailHashValues.length + this.removeEmailControlList.length;
    for (let i = 1; i <= this.existingEmailCount; i++) {
      const freqControlName = `reportFrequencyExist${i.toString()}`;
      const emailControlName = `reportEmailsExist${i.toString()}`;
      if (this.emailValuesForm.controls[emailControlName]) {
        if (
          this.emailValuesForm.controls[emailControlName].value &&
          (this.emailValuesForm.controls[freqControlName].dirty ||
            this.emailValuesForm.controls[emailControlName].dirty)
        ) {
          this.totalEmailReportChanges += 1;
        }
      }
    }
  }

  handleSave(): void {
    if (this.queueInfo.valid) {
      this.loadingStateComplete$.next(false);
      if (this.queueInfo.controls.queueReporting.value) {
        this.setTotalEmailReportChanges();
        if (this.totalEmailReportChanges === 0) {
          this.editQueue();
        } else {
          this.updateEmailReports();
        }
      } else if (this.queue.emailReports.length > 0) {
        this.totalEmailReportChanges = Object.values(this.existingEmailControlToId).length;
        this.removeAllEmailReports();
      } else {
        this.editQueue();
      }
    } else {
      this.snackbarService.openSnackBar(
        this.translate.instant("ONSIP_I18N.PLEASE_FIX_ERRORS_IN_SUBMISSION"),
        "error"
      );
    }
  }
}
